/**
 * Catalogue des champs (métriques/dimensions) d'un type de source, tel que servi
 * par `GET /api/query/metadata/:sourceType` (registry backend).
 *
 * Metrix embarque des catalogues statiques (`catalogues/ga4.ts`, `ads.ts`) ; Orion
 * a un registry backend qui porte déjà `label`, `format` et le flag `time`. On le
 * consomme donc via l'API, avec un cache module (une requête par type de source,
 * partagée entre tous les widgets et le drawer).
 */
import { useEffect, useState } from 'react';
import type { MetricFormat } from './formatValue';

export interface MetricField {
  key: string;
  label?: string;
  format?: MetricFormat;
  /** `additive` (se somme) ou `ratio` (se recompose depuis ses composants). */
  type?: string;
  /** Pour un ratio : noms des composants, renvoyés par ligne sous `_<nom>` (voir `ratios.ts`). */
  numerator?: string;
  denominator?: string;
  /** Sens favorable de la métrique : `down` = une baisse est bonne (CPC, coût/conv, rebond). Absent = `up`. */
  goodDirection?: 'up' | 'down';
}

export interface DimensionField {
  key: string;
  label?: string;
  /** Dimension temporelle : masquée du drawer, l'axe temps se pilote via la granularité. */
  time?: boolean;
}

export interface FieldCatalog {
  source_type: string;
  metrics: MetricField[];
  dimensions: DimensionField[];
  granularities: string[];
}

const cache = new Map<string, Promise<FieldCatalog | null>>();

/** Charge (et mémorise) le catalogue d'un type de source. */
export function loadFieldCatalog(sourceType: string): Promise<FieldCatalog | null> {
  const cached = cache.get(sourceType);
  if (cached) return cached;

  const promise = fetch(`/api/query/metadata/${sourceType}`, { credentials: 'include' })
    .then((res) => res.json())
    .then((json) => (json?.success ? (json.data as FieldCatalog) : null))
    .catch(() => null);

  // Un échec réseau ne doit pas être mémorisé définitivement : on purge pour retenter plus tard.
  promise.then((result) => {
    if (!result) cache.delete(sourceType);
  });

  cache.set(sourceType, promise);
  return promise;
}

/** Catalogue d'un type de source (`undefined` tant qu'il n'est pas chargé). */
export function useFieldCatalog(sourceType: string | undefined): FieldCatalog | null {
  const [catalog, setCatalog] = useState<FieldCatalog | null>(null);

  useEffect(() => {
    if (!sourceType) {
      setCatalog(null);
      return;
    }
    let cancelled = false;
    loadFieldCatalog(sourceType).then((result) => {
      if (!cancelled) setCatalog(result);
    });
    return () => {
      cancelled = true;
    };
  }, [sourceType]);

  return catalog;
}

/** Définition d'une métrique (`type`, `numerator`, `denominator`…) — indispensable à l'agrégation ratio-safe. */
export function metricDef(catalog: FieldCatalog | null, key: string | undefined): MetricField | undefined {
  if (!key) return undefined;
  return catalog?.metrics.find((m) => m.key === key);
}

/** Libellé d'une métrique : override widget prioritaire, sinon catalogue, sinon clé brute. */
export function metricLabel(
  catalog: FieldCatalog | null,
  key: string,
  overrides?: Record<string, string>,
): string {
  return overrides?.[key] || catalog?.metrics.find((m) => m.key === key)?.label || key;
}

/** Format d'affichage d'une métrique (défaut : `number`). */
export function metricFormat(catalog: FieldCatalog | null, key: string): MetricFormat {
  return catalog?.metrics.find((m) => m.key === key)?.format ?? 'number';
}

/** Sens favorable d'une métrique : override widget prioritaire, sinon défaut catalogue, sinon `up`. */
export function metricGoodDirection(
  catalog: FieldCatalog | null,
  key: string,
  override?: 'up' | 'down',
): 'up' | 'down' {
  return override ?? catalog?.metrics.find((m) => m.key === key)?.goodDirection ?? 'up';
}

/** Libellé d'une dimension (repli sur la clé brute). */
export function dimensionLabel(catalog: FieldCatalog | null, key: string): string {
  return catalog?.dimensions.find((d) => d.key === key)?.label ?? key;
}

/** Libellé d'un champ quelconque (métrique OU dimension) — utile pour les entêtes de table. */
export function fieldLabel(catalog: FieldCatalog | null, key: string): string {
  const metric = catalog?.metrics.find((m) => m.key === key);
  if (metric) return metric.label ?? key;
  return dimensionLabel(catalog, key);
}
